import { Link } from "@tanstack/react-router";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import logo from "@/assets/kslau-logo-horizontal-white.png.asset.json";
import { firm } from "@/lib/content";
import { useFirmContact } from "@/lib/hooks/use-firm-contact";
import { useSiteNav, visibleNavItems } from "@/lib/hooks/use-site-nav";
import type { NavItem } from "@/lib/navigation/types";

function visibleChildren(item: NavItem): NavItem[] {
  return (item.children ?? []).filter((child) => child.visible);
}

export function SiteFooter() {
  const contact = useFirmContact();
  const { footerMenu, footerServiceSlugs, megaMenu } = useSiteNav();

  const footerServices = footerServiceSlugs
    .map((slug) => megaMenu.services.find((s) => s.slug === slug))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  const mainSections = visibleNavItems(footerMenu).filter((s) => s.id !== "footer-legal");
  const legalSection = footerMenu.find((s) => s.id === "footer-legal");
  const legalLinks = legalSection ? visibleChildren(legalSection) : [];

  return (
    <footer className="relative bg-primary text-primary-foreground">
      <div aria-hidden="true" className="h-px w-full bg-[color:var(--gold)]/40" />
      <div className="container-page pt-10 md:pt-12 pb-20 md:pb-24">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center" aria-label={`${firm.name} home`}>
              <img src={logo.url} alt={firm.name} className="h-10 w-auto" />
            </Link>
            <ul className="mt-6 space-y-2.5 text-[15px] text-primary-foreground/85">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--gold)]" />
                <span>
                  {contact.address.line1}, {contact.address.line2}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--gold)]" />
                <a
                  href={`tel:${contact.phone.replace(/\s/g, "")}`}
                  className="hover:text-[color:var(--gold)]"
                >
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--gold)]" />
                <a href={`mailto:${contact.email}`} className="hover:text-[color:var(--gold)]">
                  {contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--gold)]" />
                <span>{contact.hours}</span>
              </li>
            </ul>
          </div>

          <nav aria-label="Services" className="lg:col-span-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--gold)]">
              Services
            </h2>
            <ul className="mt-4 space-y-2.5 text-[15px]">
              <li>
                <Link to="/services" className="font-semibold text-primary-foreground hover:text-[color:var(--gold)]">
                  All Services
                </Link>
              </li>
              {footerServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services/$slug"
                    params={{ slug: s.slug }}
                    className="text-primary-foreground/75 hover:text-[color:var(--gold)]"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {mainSections.map((section) => (
            <nav key={section.id} aria-label={section.label} className="lg:col-span-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--gold)]">
                {section.label}
              </h2>
              <ul className="mt-4 space-y-2.5 text-[15px]">
                {visibleChildren(section).map((link) => (
                  <li key={link.id}>
                    <Link
                      to={link.href ?? "/"}
                      className="text-primary-foreground/75 hover:text-[color:var(--gold)]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-primary-foreground/10 pt-6 text-sm text-primary-foreground/65 sm:flex-row sm:items-center">
          <p suppressHydrationWarning>
            © {new Date().getFullYear()} {firm.legalName}. All rights reserved.
          </p>
          {legalLinks.length > 0 && (
            <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {legalLinks.map((link, index) => (
                <span key={link.id} className="contents">
                  {index > 0 && <span aria-hidden="true">·</span>}
                  <Link to={link.href ?? "/"} className="hover:text-[color:var(--gold)]">
                    {link.label}
                  </Link>
                </span>
              ))}
            </nav>
          )}
        </div>
      </div>
    </footer>
  );
}
